/**
 * Маршрутизатор трека — свой и минимальный (решение `D119`).
 *
 * Библиотеки здесь нет и не будет: зависимостей выполнения у трека ноль
 * (решение `D34`), а `react-router` и `vue-router` названы в запрещённом
 * перечне поимённо. Всё, что нужно каркасу, — разобрать адрес, собрать адрес и
 * сообщить о смене; это три функции, а не пакет.
 *
 * Адрес — **хеш**, а не путь. Кабинет раздаётся статикой, и глубокая ссылка
 * вида `/c/…` вернула бы отказ сервера раньше, чем приложение загрузится;
 * `try_files` — правка чужой настройки в чужом файле. Цена названа решением:
 * адреса некрасивы, а переход на History API позже будет сменой формы адреса.
 *
 * ПЕРЕВОРОТ блока `WEB-3.2` (спецификация §6, ответ владельца `Б1`-а). Было:
 * «адрес — хеш, а не путь» без исключений. Стало: кабинет по-прежнему живёт
 * хешем, а **одна** ветка разбирает путь — ссылку публичного отслеживания
 * `/t/<токен>`, которую сервер раздаёт путём. Ссылки, уже розданные заказчикам,
 * обязаны открываться как есть; второго разборщика адреса при этом нет — путь
 * разбирается здесь же, рядом с хешем.
 *
 * Активная фирма живёт **в адресе** (решение `D120`). Второе место, где живёт
 * «выбранная фирма», разошлось бы с первым молча, а перезагрузка и кнопки
 * браузера перестали бы работать без своего хранилища, которого в треке нет.
 *
 * ПЕРЕВОРОТ блока `WEB-2.1` (спецификация §6, решение формы `Б1`). Было:
 * маршруты блоков `WEB-1.2`, `WEB-1.3` и ветка ссылки `WEB-3.2`. Стало: плюс
 * четыре маршрута кабинета логиста — «Мои объявления» `#/c/<фирма>/cargo`,
 * новая запись `…/cargo/new`, карточка `…/cargo/<id>` и правка
 * `…/cargo/<id>/edit`. Сегмент `cargo` назван один раз; разборщик тот же.
 * Что прежний ловил, а новый не ловит: адреса этих четырёх форм прежде
 * разбирались в `notFound`. Больше ничего — неизвестная форма адреса
 * по-прежнему `notFound`.
 *
 * Адрес собирается **только здесь**. Строитель, заведённый рядом, — второе
 * место, где живёт форма адреса, и ссылка, собранная им, перестанет
 * разбираться в тот день, когда форма изменится.
 */

/** Маршруты каркаса. Перечень закрыт: неизвестный адрес — это `notFound`. */
export type Route =
  | { readonly kind: 'login' }
  | { readonly kind: 'companies' }
  | { readonly kind: 'company'; readonly companyId: string }
  | { readonly kind: 'works'; readonly companyId: string }
  // Маршруты кабинета — блок `WEB-1.2`. Размеченное объединение растёт, а
  // второго разборщика адреса не заводится: форма адреса живёт в одном месте.
  | { readonly kind: 'profile' }
  | { readonly kind: 'members'; readonly companyId: string }
  | { readonly kind: 'memberRights'; readonly companyId: string; readonly membershipId: string }
  | { readonly kind: 'companyProfile'; readonly companyId: string }
  | { readonly kind: 'memberDocuments'; readonly companyId: string; readonly membershipId: string }
  // Маршруты домена транспорта — блок `WEB-1.3`. То же размеченное объединение
  // растёт тремя, второго разборщика адреса по-прежнему нет.
  | { readonly kind: 'fleet'; readonly companyId: string }
  | { readonly kind: 'vehicleService'; readonly companyId: string; readonly vehicleId: string }
  | { readonly kind: 'vinClaims'; readonly companyId: string }
  // Маршруты кабинета логиста — блок `WEB-2.1`. Объявление называется своим
  // полем, как запись членства и запись реестра: два адреса разных объявлений
  // одной фирмы — разные места.
  | { readonly kind: 'cargoListings'; readonly companyId: string }
  | { readonly kind: 'cargoListingNew'; readonly companyId: string }
  | { readonly kind: 'cargoListing'; readonly companyId: string; readonly listingId: string }
  | { readonly kind: 'cargoListingEdit'; readonly companyId: string; readonly listingId: string }
  | { readonly kind: 'notFound' };

export type RouteHandler = (route: Route) => void;

/** Сегменты адреса. Названы здесь один раз — второго их экземпляра нет. */
const COMPANY_SEGMENT = 'c';
const WORKS_SEGMENT = 'works';
const LOGIN_SEGMENT = 'login';

// Сегменты кабинета — блок `WEB-1.2`. Названы здесь по одному разу, рядом с
// прежними: второго места, где живёт форма адреса, у трека нет.
const PROFILE_SEGMENT = 'profile';
const MEMBERS_SEGMENT = 'members';
const RIGHTS_SEGMENT = 'rights';
const DOCUMENTS_SEGMENT = 'documents';

// Сегменты домена транспорта — блок `WEB-1.3`. Там же и по одному разу.
const FLEET_SEGMENT = 'fleet';
const SERVICE_SEGMENT = 'service';
const VIN_CLAIMS_SEGMENT = 'vin-claims';

// Сегменты кабинета логиста — блок `WEB-2.1`. Там же и по одному разу.
const CARGO_SEGMENT = 'cargo';
const NEW_SEGMENT = 'new';
const EDIT_SEGMENT = 'edit';

const ROOT_PATH = '#/';

/** Первый сегмент пути публичной ссылки — блок `WEB-3.2`. Назван один раз. */
const TRACKING_SEGMENT = 't';

function segmentsOf(hash: string): string[] {
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const parts: string[] = [];

  // Перебором, а не отбором коллекции: любая форма `.filter(` в рукописном
  // исходнике трека запрещена сторожем «лента не пересобирается в клиенте», и
  // послабления ради удобства разбора адреса он не знает.
  for (const part of withoutHash.split('/')) {
    if (part !== '') {
      parts.push(part);
    }
  }

  return parts;
}

/**
 * Адрес экрана фирмы. Единственное место, где он собирается.
 *
 * Тест, повторяющий вывод строителя литералом, стал бы вторым строителем —
 * поэтому и в наборе сверяется обратимость, а не написанная строка.
 */
export function companyPath(companyId: string): string {
  return `${ROOT_PATH}${COMPANY_SEGMENT}/${companyId}`;
}

/** Адрес ленты работ выбранной фирмы. Тот же единственный строитель. */
export function worksPath(companyId: string): string {
  return `${companyPath(companyId)}/${WORKS_SEGMENT}`;
}

/** Адрес профиля пользователя. Фирмы в нём нет: профиль принадлежит человеку. */
export function profilePath(): string {
  return `${ROOT_PATH}${PROFILE_SEGMENT}`;
}

/** Адрес списка сотрудников выбранной фирмы. */
export function membersPath(companyId: string): string {
  return `${companyPath(companyId)}/${MEMBERS_SEGMENT}`;
}

/** Адрес слоёв прав одной записи членства. */
export function memberRightsPath(companyId: string, membershipId: string): string {
  return `${membersPath(companyId)}/${membershipId}/${RIGHTS_SEGMENT}`;
}

/** Адрес профиля фирмы: реквизиты и её документы. */
export function companyProfilePath(companyId: string): string {
  return `${companyPath(companyId)}/${PROFILE_SEGMENT}`;
}

/** Адрес документов одной записи членства. */
export function memberDocumentsPath(companyId: string, membershipId: string): string {
  return `${membersPath(companyId)}/${membershipId}/${DOCUMENTS_SEGMENT}`;
}

/** Адрес реестра транспорта выбранной фирмы. */
export function fleetPath(companyId: string): string {
  return `${companyPath(companyId)}/${FLEET_SEGMENT}`;
}

/** Адрес экрана сервиса одной записи реестра: состояние, переводы, журнал. */
export function vehicleServicePath(companyId: string, vehicleId: string): string {
  return `${fleetPath(companyId)}/${vehicleId}/${SERVICE_SEGMENT}`;
}

/** Адрес заявок фирмы на освобождение VIN — обе стороны одним списком. */
export function vinClaimsPath(companyId: string): string {
  return `${companyPath(companyId)}/${VIN_CLAIMS_SEGMENT}`;
}

/** Адрес «Моих объявлений» о грузе выбранной фирмы. */
export function cargoListingsPath(companyId: string): string {
  return `${companyPath(companyId)}/${CARGO_SEGMENT}`;
}

/** Адрес формы нового объявления о грузе. */
export function cargoListingNewPath(companyId: string): string {
  return `${cargoListingsPath(companyId)}/${NEW_SEGMENT}`;
}

/** Адрес карточки объявления: offer'ы и доска заказа. */
export function cargoListingPath(companyId: string, listingId: string): string {
  return `${cargoListingsPath(companyId)}/${listingId}`;
}

/** Адрес формы правки объявления — та же форма, заполненная. */
export function cargoListingEditPath(companyId: string, listingId: string): string {
  return `${cargoListingPath(companyId, listingId)}/${EDIT_SEGMENT}`;
}

/**
 * Адрес маршрута. Внутренний: наружу объявлены строители словаря имён.
 *
 * `notFound` собственного адреса не имеет — это не место, а промах мимо места,
 * и переход на него означает возврат в корень кабинета.
 */
function pathOf(route: Route): string {
  switch (route.kind) {
    case 'login':
      return `${ROOT_PATH}${LOGIN_SEGMENT}`;
    case 'company':
      return companyPath(route.companyId);
    case 'works':
      return worksPath(route.companyId);
    case 'profile':
      return profilePath();
    case 'members':
      return membersPath(route.companyId);
    case 'memberRights':
      return memberRightsPath(route.companyId, route.membershipId);
    case 'companyProfile':
      return companyProfilePath(route.companyId);
    case 'memberDocuments':
      return memberDocumentsPath(route.companyId, route.membershipId);
    case 'fleet':
      return fleetPath(route.companyId);
    case 'vehicleService':
      return vehicleServicePath(route.companyId, route.vehicleId);
    case 'vinClaims':
      return vinClaimsPath(route.companyId);
    case 'cargoListings':
      return cargoListingsPath(route.companyId);
    case 'cargoListingNew':
      return cargoListingNewPath(route.companyId);
    case 'cargoListing':
      return cargoListingPath(route.companyId, route.listingId);
    case 'cargoListingEdit':
      return cargoListingEditPath(route.companyId, route.listingId);
    case 'companies':
    case 'notFound':
      return ROOT_PATH;
  }
}

/**
 * Разбор адреса в маршрут.
 *
 * Пустой адрес — это вход в приложение, а не промах мимо маршрута: экран «не
 * найдено» на первом же открытии кабинета был бы самой заметной ошибкой
 * роутера. Неизвестная форма адреса даёт объявленный маршрут `notFound` —
 * клиент говорит «своего маршрута я не нашёл» и ничего не решает ни о правах,
 * ни о существовании объекта: и то и другое знает сервер.
 */
export function parseRoute(hash: string): Route {
  const parts = segmentsOf(hash);
  const [first, second, third, fourth, fifth] = parts;

  if (first === undefined) {
    return { kind: 'companies' };
  }

  if (first === LOGIN_SEGMENT && parts.length === 1) {
    return { kind: 'login' };
  }

  if (first === PROFILE_SEGMENT && parts.length === 1) {
    return { kind: 'profile' };
  }

  if (first === COMPANY_SEGMENT && second !== undefined) {
    if (parts.length === 2) {
      return { kind: 'company', companyId: second };
    }

    if (parts.length === 3 && third === WORKS_SEGMENT) {
      return { kind: 'works', companyId: second };
    }

    if (parts.length === 3 && third === PROFILE_SEGMENT) {
      return { kind: 'companyProfile', companyId: second };
    }

    if (parts.length === 3 && third === MEMBERS_SEGMENT) {
      return { kind: 'members', companyId: second };
    }

    if (parts.length === 3 && third === FLEET_SEGMENT) {
      return { kind: 'fleet', companyId: second };
    }

    if (parts.length === 3 && third === VIN_CLAIMS_SEGMENT) {
      return { kind: 'vinClaims', companyId: second };
    }

    if (parts.length === 3 && third === CARGO_SEGMENT) {
      return { kind: 'cargoListings', companyId: second };
    }

    // Новая запись — своя форма, а не объявление с идентификатором `new`:
    // сегмент проверяется раньше разбора идентификатора.
    if (parts.length === 4 && third === CARGO_SEGMENT && fourth === NEW_SEGMENT) {
      return { kind: 'cargoListingNew', companyId: second };
    }

    if (parts.length === 4 && third === CARGO_SEGMENT && fourth !== undefined) {
      return { kind: 'cargoListing', companyId: second, listingId: fourth };
    }

    if (parts.length === 5 && third === CARGO_SEGMENT && fourth !== undefined && fifth === EDIT_SEGMENT) {
      return { kind: 'cargoListingEdit', companyId: second, listingId: fourth };
    }

    if (parts.length === 5 && third === MEMBERS_SEGMENT && fourth !== undefined) {
      if (fifth === RIGHTS_SEGMENT) {
        return { kind: 'memberRights', companyId: second, membershipId: fourth };
      }

      if (fifth === DOCUMENTS_SEGMENT) {
        return { kind: 'memberDocuments', companyId: second, membershipId: fourth };
      }
    }

    if (
      parts.length === 5
      && third === FLEET_SEGMENT
      && fourth !== undefined
      && fifth === SERVICE_SEGMENT
    ) {
      return { kind: 'vehicleService', companyId: second, vehicleId: fourth };
    }
  }

  return { kind: 'notFound' };
}

/**
 * Токен публичной ссылки из **пути** документа либо `null`.
 *
 * Ссылка — ровно два непустых сегмента, первый из них `t`; всё прочее — не
 * ссылка, и решает дальше кабинет. Годность токена клиент не проверяет и о
 * неверной ссылке не говорит: существует ли она, знает только сервер.
 */
export function parseTrackingToken(pathname: string): string | null {
  const parts = segmentsOf(pathname);
  const [first, second] = parts;

  if (parts.length === 2 && first === TRACKING_SEGMENT && second !== undefined) {
    return second;
  }

  return null;
}

/** Маршрут, стоящий в адресной строке сейчас. */
export function currentRoute(): Route {
  return parseRoute(location.hash);
}

/** Запись членства из маршрута либо `null` — маршрут её не называет. */
export function membershipIdOf(route: Route): string | null {
  return 'membershipId' in route ? route.membershipId : null;
}

/** Запись реестра транспорта из маршрута либо `null` — та же форма. */
export function vehicleIdOf(route: Route): string | null {
  return 'vehicleId' in route ? route.vehicleId : null;
}

/** Объявление о грузе из маршрута либо `null` — та же форма. */
export function listingIdOf(route: Route): string | null {
  return 'listingId' in route ? route.listingId : null;
}

function sameRoute(left: Route, right: Route): boolean {
  if (left.kind !== right.kind) {
    return false;
  }

  const leftCompany = 'companyId' in left ? left.companyId : null;
  const rightCompany = 'companyId' in right ? right.companyId : null;

  // Запись членства различает маршруты так же, как фирма: два адреса прав
  // разных сотрудников одной фирмы — разные места, и молчание о переходе
  // между ними оставило бы на экране права предыдущего. Запись реестра
  // транспорта — тем же доводом: два адреса сервиса разных машин — разные
  // места, и без различения на экране остался бы журнал предыдущей.
  // Объявление о грузе — тем же доводом (блок `WEB-2.1`).
  return (
    leftCompany === rightCompany
    && membershipIdOf(left) === membershipIdOf(right)
    && vehicleIdOf(left) === vehicleIdOf(right)
    && listingIdOf(left) === listingIdOf(right)
  );
}

const handlers: RouteHandler[] = [];

/**
 * Последний сообщённый маршрут.
 *
 * Он нужен затем, что о смене адреса сообщается **один раз на маршрут**:
 * браузер шлёт событие на каждую запись адресной строки, а подписчику важна
 * смена места, а не число записей. Повтор того же маршрута перерисовал бы
 * экран без причины и на переходе «туда и обратно» дал бы два обращения к
 * серверу вместо одного.
 */
let announced: Route | null = null;

function announce(): void {
  const route = currentRoute();

  if (announced !== null && sameRoute(announced, route)) {
    return;
  }

  announced = route;

  for (const handler of handlers) {
    handler(route);
  }
}

window.addEventListener('hashchange', () => {
  announce();
});

/**
 * Подписка на смену маршрута.
 *
 * Источник один на оба случая: и переход по кнопке приложения, и адрес,
 * набранный руками, приходят сюда одним и тем же событием. Иначе глубокая
 * ссылка стала бы отдельным путём со своим поведением.
 */
export function onRouteChange(handler: RouteHandler): void {
  handlers.push(handler);
}

/**
 * Переход: адрес меняется, документ не перезагружается.
 *
 * Присваиванием, а не заменой записи истории: кнопка «назад» обязана
 * возвращать предыдущий маршрут. Повтор текущего адреса пропускается — иначе
 * «назад» отматывал бы один и тот же маршрут столько раз, сколько раз по нему
 * перешли.
 */
export function navigate(route: Route): void {
  const next = pathOf(route);

  if (location.hash === next) {
    return;
  }

  location.hash = next;
}
